import { useCallback, useContext, useEffect, useRef } from 'react'
import * as React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'

import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import IconButton from 'LuxKit/Button/IconButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import SearchMenuListItem from 'components/Search/SearchForm/SearchMenu/SearchMenuListItem'
import {
  GlobalSearchDispatchContext,
  GlobalSearchStateContext,
} from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useGlobalSearchTypeahead from 'hooks/GlobalSearch/useGlobalSearchTypeahead'
import useToggle from 'hooks/useToggle'
import noop from 'lib/function/noop'
import { TRIP_PLACE_TYPEAHEAD_TYPES } from 'tripPlanner/config/search'

interface PlaceSelection {
  id: string
  name: string
}

interface Props {
  label?: string
  inputValue: string
  onInputChange: (value: string) => void
  placeSelection?: PlaceSelection
  onSelect: (value: PlaceSelection) => void
  onClear?: () => void
  autoFocus?: boolean
  autoSelect?: boolean
  disabled?: boolean
  isInvalidState?: boolean
}

function TripPlaceFieldDesktop({
  label,
  inputValue,
  onInputChange,
  onSelect,
  onClear: onClearProp = noop,
  autoFocus,
  autoSelect,
  disabled,
  isInvalidState,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const { value: isActive, on: activate, off: deactivate } = useToggle()
  const intl = useIntl()

  const searchPlaceholder = intl.formatMessage({
    id: 'PwYA-DqrTcaX6GxUku8Q',
    defaultMessage: 'Search by city',
    description: 'Placeholder text for the destination search input field',
  })

  const clearAriaLabel = intl.formatMessage({
    id: 'm4vIx7PyGlqRoK7IL1wT',
    defaultMessage: 'Clear',
    description: 'Aria label for the clear button on the destination search input',
  })

  const selectLocationMessage = intl.formatMessage({
    id: 'uTk9noSzhMukLWhgAyHO',
    defaultMessage: 'Please select a location from the dropdown',
    description: 'Validation error when user types but does not select a destination from dropdown suggestions',
  })

  const spellingHintMessage = intl.formatMessage({
    id: 'p05dRO0_phJdgLP2-9_C',
    defaultMessage: 'Check your spelling, or try a new location.',
    description: 'Hint shown below the not-found message when location search returns no results',
  })

  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const searchState = useContext(GlobalSearchStateContext)
  const { suggestedSearchItems } = searchState

  const { isLoading } = useGlobalSearchTypeahead({
    search: inputValue,
    typeaheadTypes: TRIP_PLACE_TYPEAHEAD_TYPES,
  })

  // Don't want to active the dropdown on the initial focus
  const shouldActivateOnFocus = useRef(false)

  useEffect(() => {
    if (inputRef.current) {
      if (autoSelect) {
        inputRef.current.select()
      } else if (autoFocus) {
        inputRef.current.focus()
      }
    }
    shouldActivateOnFocus.current = true
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClear = useCallback(() => {
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_SUGGESTED_SEARCH_ITEMS,
      searchItems: [],
    })
    onInputChange('')
    deactivate()
    onClearProp()
  }, [globalSearchDispatch, onInputChange, deactivate, onClearProp])

  const onInputChangeInternal = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value
      if (value.length === 0) {
        onClear()
      } else {
        onInputChange(value)
        activate()
      }
    },
    [onInputChange, activate, onClear],
  )

  const onSelectItem = useCallback(
    (changedSearchItem: App.SearchItem) => {
      onSelect({
        id: changedSearchItem.value,
        name: changedSearchItem.format.mainText,
      })
      deactivate()
    },
    [onSelect, deactivate],
  )

  const hasInput = inputValue.length > 0
  const onInputFocus = useCallback(() => {
    if (hasInput && shouldActivateOnFocus.current) {
      activate()
    }
  }, [activate, hasInput])

  const isEmptyResult =
    hasInput && suggestedSearchItems.length === 0 && !isLoading

  const showInvalidStateMessage = !isActive && isInvalidState

  return (
    <div>
      <TextInput
        label={label}
        ref={inputRef}
        placeholder={searchPlaceholder}
        value={inputValue}
        onChange={onInputChangeInternal}
        onFocus={onInputFocus}
        disabled={disabled}
        endIcon={
          hasInput && (
            <IconButton kind="tertiary" onClick={onClear} aria-label={clearAriaLabel}>
              <LineTimesIcon />
            </IconButton>
          )
        }
        manualError={showInvalidStateMessage}
        invalidErrorMessage={
          showInvalidStateMessage ?
            selectLocationMessage :
            undefined
        }
      />
      <DropdownList
        size="M"
        triggerRef={inputRef}
        anchorRef={inputRef}
        open={isActive}
        onClose={deactivate}
        placement="bottom-start"
      >
        {isLoading && <LoadingIndicator inline />}
        {!isLoading &&
          suggestedSearchItems.map((item) => (
            <SearchMenuListItem
              key={item.value}
              searchItem={item}
              onClick={onSelectItem}
            />
          ))}
        {isEmptyResult && (
          <VerticalSpacer gap={4}>
            <BodyText variant="medium" colour="neutral-two">
              <FormattedMessage
                id="ZH15kj4wYEnLTTc5ckng"
                defaultMessage="Sorry, we couldn’t find <bold>{inputValue}</bold>."
                description="Error message when location search returns no results for the entered text"
                values={{
                  inputValue,
                  bold: (chunks) => <b>{chunks}</b>,
                }}
              />
            </BodyText>
            <Caption variant="medium">
              {spellingHintMessage}
            </Caption>
          </VerticalSpacer>
        )}
      </DropdownList>
    </div>
  )
}

export default TripPlaceFieldDesktop
